import type { HttpClient } from '../http/HttpClient'
import { taxIdSubline } from '@/utils/invoiceDisplay'
import type { GlobalSearchRepository, GlobalSearchResult, SearchQueryKind, SearchRecord } from '@/domain/search/GlobalSearch'

interface RawBase {
  id: number
  status: string
  provider_name?: string | null
  society_id: number
  society_name: string | null
  match_rank: number
}

interface RawResolvedStatus { status_label?: string | null, status_color?: string | null }
interface RawInvoice extends RawBase, RawResolvedStatus { number: string | null, tax_authority_uid: string | null, cufe?: string | null, cae?: string | null, type?: string | null, total: number | null, currency: string | null, emission_date: string | null }
interface RawRequest extends RawBase, RawResolvedStatus { request_number: number, description: string | null, type: string, budget: number | null, currency: string | null }
interface RawProvider extends RawBase { name: string, national_id: string | null, type?: string | null, can_open_detail?: boolean }
interface RawPurchaseOrder extends RawBase { number: string | null, total: number | null, currency: string | null, emission_date: string | null }
interface RawUser extends Omit<RawBase, 'status' | 'provider_name'> { name: string, email: string | null, role: string | null, area: string | null }
interface RawPayment extends RawBase { number: number | string | null, total: number | null, currency: string | null, programmed_payment_date: string | null }
interface RawContract extends RawBase { number: number | null, name: string, reference: string | null, total: number | null, currency: string | null }
interface RawReception extends Omit<RawBase, 'status'> { number: number | null, description: string | null }
interface RawBankBatch extends Omit<RawBase, 'provider_name'> { number: number | null, name: string | null, total: number | null, currency: string | null, configuration_name: string | null, bank_name: string | null }
interface RawTransaction extends Omit<RawBase, 'provider_name'> { number: number | null, total: number | null, currency: string | null, description: string | null, merchant: string | null }
interface RawCatalog extends Omit<RawBase, 'status' | 'provider_name'> { kind: string, code: string | null, name: string | null }

interface RawResponse {
  query: { kind: SearchQueryKind, normalized: string }
  results: {
    invoices?: RawInvoice[]
    requests?: RawRequest[]
    providers?: RawProvider[]
    purchase_orders?: RawPurchaseOrder[]
    payments?: RawPayment[]
    users?: RawUser[]
    contracts?: RawContract[]
    receptions?: RawReception[]
    bank_batches?: RawBankBatch[]
    transactions?: RawTransaction[]
    accounting?: RawCatalog[]
    settings?: RawCatalog[]
  }
}

function record(raw: Omit<RawBase, 'status'> & { status?: string | null }, fields: Partial<SearchRecord> & { title: string }): SearchRecord {
  return {
    id: raw.id,
    providerName: raw.provider_name ?? null,
    nationalId: null,
    description: null,
    amount: null,
    currency: null,
    societyName: raw.society_name,
    email: null,
    area: null,
    role: null,
    status: raw.status ?? null,
    statusLabel: null,
    statusColor: null,
    kind: null,
    code: null,
    type: null,
    documentId: null,
    canOpenDetail: true,
    matchRank: raw.match_rank,
    ...fields,
  }
}

export class HttpGlobalSearchRepository implements GlobalSearchRepository {
  constructor(private readonly http: HttpClient) {}

  async search(query: string, signal?: AbortSignal): Promise<GlobalSearchResult> {
    const request = this.http.url('search').params({ q: query })
    if (signal) request.signal(signal)
    const raw = await request.get<RawResponse>()
    const results = raw.results
    return {
      kind: raw.query.kind,
      normalized: raw.query.normalized,
      results: {
        invoices: (results.invoices ?? []).map((r) => record(r, {
          title: r.number || r.tax_authority_uid || r.cufe || `#${r.id}`, amount: r.total, currency: r.currency,
          code: r.number || null, type: r.type ?? null,
          documentId: taxIdSubline({ number: r.number, tax_authority_uid: r.tax_authority_uid, cufe: r.cufe, cae: r.cae }) || null,
          statusLabel: r.status_label ?? null, statusColor: r.status_color ?? null,
        })),
        payments: (results.payments ?? []).map((r) => record(r, {
          title: `#${r.number ?? r.id}`, amount: r.total, currency: r.currency,
        })),
        providers: (results.providers ?? []).map((r) => record(r, {
          title: r.name, nationalId: r.national_id, type: r.type ?? null, canOpenDetail: r.can_open_detail ?? false,
        })),
        purchaseOrders: (results.purchase_orders ?? []).map((r) => record(r, {
          title: r.number || `#${r.id}`, amount: r.total, currency: r.currency,
        })),
        requests: (results.requests ?? []).map((r) => record(r, {
          title: `#${r.request_number}`, description: r.description, amount: r.budget, currency: r.currency,
          statusLabel: r.status_label ?? null, statusColor: r.status_color ?? null,
        })),
        users: (results.users ?? []).map((r) => record(r, {
          title: r.name, email: r.email, area: r.area, role: r.role,
        })),
        contracts: (results.contracts ?? []).map((r) => record(r, {
          title: r.name || `#${r.number ?? r.id}`, code: r.number === null ? null : String(r.number), amount: r.total, currency: r.currency,
        })),
        receptions: (results.receptions ?? []).map((r) => record(r, {
          title: `#${r.number ?? r.id}`, description: r.description,
        })),
        bankBatches: (results.bank_batches ?? []).map((r) => record(r, {
          title: r.name || `#${r.number ?? r.id}`, description: [r.configuration_name, r.bank_name].filter(Boolean).join(' · ') || null,
          amount: r.total, currency: r.currency,
        })),
        transactions: (results.transactions ?? []).map((r) => record(r, {
          title: r.merchant || r.description || `#${r.number ?? r.id}`, description: r.merchant ? r.description : null,
          amount: r.total, currency: r.currency,
        })),
        accounting: (results.accounting ?? []).map((r) => record(r, {
          title: r.name || r.code || `#${r.id}`, kind: r.kind, code: r.code,
        })),
        settings: (results.settings ?? []).map((r) => record(r, {
          title: r.name || `#${r.id}`, kind: r.kind, code: r.code,
        })),
      },
    }
  }
}
